import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { Mapper, MapperBuilder } from '../../helpers/mapper';
import { RateGridFilterRequest } from '../../interfaces/rate-grid-filter-request.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';
import { ExcelImportService } from '../../services/excel-import.service';
import { nameof } from '../../helpers/nameof';

@Component({
  selector: 'app-care-allowance-rate-list',
  templateUrl: './care-allowance-rate-list.component.html',
  styleUrl: './care-allowance-rate-list.component.scss'
})
export class CareAllowanceRateListComponent extends BaseCardSetListComponent implements OnInit {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  isApprovedRatesOnly: boolean;

  constructor(
    protected router: Router,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService,
    private excelExportService: ExcelExportService,
    private excelImportService: ExcelImportService,
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.careAllowanceRateList,
      () => API_ENDPOINTS.dashboard.getCareAllowanceRates,
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.financialYearFilter, null, nameof<RateGridFilterRequest>('financialYears'))
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.rateStatusFilter, null, nameof<RateGridFilterRequest>('rateStatuses'))
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.rateTypeFilter, null, nameof<RateGridFilterRequest>('rateTypes'))
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.placementTypeFilter, null, nameof<RateGridFilterRequest>('placementTypes'))
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.placementLevelFilter, null, nameof<RateGridFilterRequest>('placementLevels'))
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.ageMinFilter)
        .MapFrom(FILTER_QUESTION_CODES.careAllowanceRateListForm.ageMaxFilter)
        .BuildMapper();
  }

  ngOnInit(): void {
    if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceRate.list.ratesApproved)) {
      this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceApprovedRateListConfig;
      this.isApprovedRatesOnly = true;
    }
    else if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceRate.list.ratesPendingApproval)) {
      this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowancePendingRateListConfig;
      this.isApprovedRatesOnly = false;
    }
  }

  formLoad(event: any): void {
    const filterRequest = this.getRateFilterRequest();

    this.loadGridDataOnFormLoad(filterRequest);
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.careAllowanceRateListForm.rateListFilter:
        {
          const filterRequest = this.getRateFilterRequest();
          this.loadGridData(filterRequest);
          break;
        }
    }
  }

  actionButton_rateGrid_datagrid_new_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceRate.prefix]);
  }

  actionButton_rateGrid_datagrid_edit_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceRate.prefix, event.event.data.id]);
  }

  actionButton_rateGrid_datagrid_view_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceRate.prefixViewPending, event.event.data.id]);
  }

  actionButton_rateGrid_datagrid_custom_exportCsv_Clicked(event: any): void {
    const filterRequest = this.getRateFilterRequest();
    this.excelExportService.exportCareAllowanceRatesToCsv(filterRequest);
  }

  actionButton_rateGrid_datagrid_custom_getUploadTemplate_Clicked(event: any): void {
    this.excelExportService.exportCareAllowanceRateUploadTemplate();
  }

  actionButton_rateGrid_datagrid_custom_bulkUpload_Clicked(event: any): void {
    const element = document.getElementById('fileInputRates') as HTMLElement;
    element.click();
  }

  actionButton_rateGrid_datagrid_custom_requestDeletion_Clicked(event: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.careAllowanceRateList.confirmDeleteRequest)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            const selectedIds = event.event.data.selectedItems.map(row => row.id);
            const endpoint = API_ENDPOINTS.careAllowanceRate.requestDelete;
            this.registerSubscription(
              this.appDataService.post(endpoint, selectedIds)
                .pipe(
                  take(1),
                  this.errorService.errorHandler(),
                  finalize(() => this.spinnerService.hideSpinner())
                )
                .subscribe(data => {
                  if (data?.status === SAVE_RESULT_STATUS.success) {
                    if (SERVER_ERROR_POP_UP_CODES.careAllowanceRateDeletionRequest.includes(data.message)) {
                      this.activeDialog = data.message;
                      this.dialogService.showDialog(this.activeDialog);
                    }
                    else {
                      this.reloadPage();
                    }
                  }
                })
            );
            break;
          }
        }
      });
  }

  actionButton_rateGrid_datagrid_custom_approveRates_Clicked(event: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.careAllowanceRateList.confirmApprovalRequest)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            const selectedIds = event.event.data.selectedItems.map(row => row.id);
            const endpoint = API_ENDPOINTS.careAllowanceRate.approvePendingChanges;
            this.registerSubscription(
              this.appDataService.post(endpoint, selectedIds)
                .pipe(
                  take(1),
                  this.errorService.errorHandler(),
                  finalize(() => this.spinnerService.hideSpinner())
                )
                .subscribe(data => {
                  if (data?.status === SAVE_RESULT_STATUS.success) {
                    if (SERVER_ERROR_POP_UP_CODES.careAllowanceRateApproveRequest.includes(data.message)) {
                      this.activeDialog = data.message;
                      this.dialogService.showDialog(this.activeDialog);
                    }
                    else {
                      this.reloadPage();
                    }
                  }
                })
            );
            break;
          }
        }
      });
  }

  actionButton_rateGrid_datagrid_custom_cancelPending_Clicked(event: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.careAllowanceRateList.confirmCancelPendingChanges)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            const selectedIds = event.event.data.selectedItems.map(row => row.id);
            const endpoint = API_ENDPOINTS.careAllowanceRate.cancelPendingChanges;
            this.registerSubscription(
              this.appDataService.post(endpoint, selectedIds)
                .pipe(
                  take(1),
                  this.errorService.errorHandler(),
                  finalize(() => this.spinnerService.hideSpinner())
                )
                .subscribe(data => {
                  if (data?.status === SAVE_RESULT_STATUS.success) {
                    if (SERVER_ERROR_POP_UP_CODES.careAllowanceRateCancelPendingChanges.includes(data.message)) {
                      this.activeDialog = data.message;
                      this.dialogService.showDialog(this.activeDialog);
                    }
                    else {
                      this.reloadPage();
                    }
                  }
                })
            );
            break;
          }
        }
      });
  }

  dialogButton_deletionRequestFailed_Clicked(event: any): void {
    // Nothing to do.
  }

  dialogButton_deletionRequestFailedPendingChanges_Clicked(event: any): void {
    // Nothing to do.
  }

  dialogButton_approvalFailed_Clicked(event: any): void {
    // Nothing to do.
  }

  dialogButton_cancelFailed_Clicked(event: any): void {
    // Nothing to do.
  }

  importRatesFromCsv(event: any): void {
    this.excelImportService.importCareAllowanceRatesFromCsv(event.target.files[0])
      .pipe(take(1))
      .subscribe(() => {
        this.loadGridData(this.getRateFilterRequest());
      });
  }

  private getRateFilterRequest(): RateGridFilterRequest {
    const approvedRateStatusFilter = [REFERENCE_DATA_IDS.careAllowanceRateStatus.approved];
    const nonApprovedRateStatusFilter = [
      REFERENCE_DATA_IDS.careAllowanceRateStatus.pendingNew,
      REFERENCE_DATA_IDS.careAllowanceRateStatus.pendingUpdate,
      REFERENCE_DATA_IDS.careAllowanceRateStatus.pendingDelete
    ];

    const filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.careAllowanceRateListForm.rates,
      PAGE_NUMBERS.careAllowanceRateListForm.rates,
      SECTION_CODES.careAllowanceRateListForm.rates,
      QUESTION_CODES.careAllowanceRateListForm.rateListFilter
    );

    let filterRequest: RateGridFilterRequest = {};
    if (filterModel != null) {
      filterRequest = this.filterQuestionToFilterModelMapper.Map(filterModel);
    }

    if (filterRequest.rateStatuses == null) {
      filterRequest.rateStatuses = this.isApprovedRatesOnly
        ? approvedRateStatusFilter
        : nonApprovedRateStatusFilter;
    }

    return filterRequest;
  }
}
